import { Button, List, Space, theme, Typography, Tooltip } from "antd";
import { useApplicationContext } from "src/hooks/useApplication";
import { SearchContext } from "src/search/useSearchContext";
import {
  InfoCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { CustomTooltip } from "src/components/CustomTooltip";

const { Text, Title } = Typography;

type Explanation = Exclude<
  Exclude<
    SearchContext["entitiesList" | "aggregations"]["data"],
    undefined
  >["meta"]["explanation"],
  undefined
>;

type Reason = Exclude<Explanation[number]["reasons"], undefined>[number];

export function EmptyResultExplanation(props: {
  explanation: Explanation;
  searchContext?: SearchContext;
}) {
  const app = useApplicationContext();
  const { token } = theme.useToken();

  const hasFilters =
    (props.searchContext?.params.state.filters?.length || 0) > 0;
  const hasSearch = Boolean(props.searchContext?.params.state.search);

  if (
    props.explanation !== undefined &&
    props.explanation.length > 0 &&
    app.isSuperAdmin
  ) {
    const data = props.explanation.map((exp) => ({
      title: `${exp.entity} is not populated`,
      reasons: exp.providable
        ? (exp.reasons || []).map(reasonToVerbiage)
        : ["No integration is configured to provide it."],
    }));

    const tooltipContent = (
      <div style={{ maxWidth: 400 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {/* Filters and Search Status */}
          {(hasFilters || hasSearch) && (
            <Space size="large" style={{ flexWrap: "wrap" }}>
              {hasFilters && (
                <Space>
                  <FilterOutlined />
                  <Text>Has Filters</Text>
                </Space>
              )}
              {hasSearch && (
                <Space>
                  <SearchOutlined />
                  <Text>Has Search Term</Text>
                </Space>
              )}
            </Space>
          )}

          {/* Technical Details */}
          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Possible Reasons:
            </Text>
            {data.map((item, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <Text
                  strong
                  style={{ display: "block", color: token.colorTextLabel }}
                >
                  {item.title}
                </Text>
                {item.reasons.map((reason, reasonIdx) => (
                  <Text
                    key={reasonIdx}
                    type="secondary"
                    style={{ display: "block", paddingLeft: 16 }}
                  >
                    • {reason}
                  </Text>
                ))}
              </div>
            ))}
          </div>

          {/* Footer */}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Contact your administrator if you believe this is unexpected
          </Text>
        </Space>
      </div>
    );

    return (
      <div
        style={{
          padding: "1rem",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: token.colorBgElevated,
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
        }}
      >
        <Space>
          <Text>No Results Found</Text>
          <CustomTooltip
            content={tooltipContent}
            placement="right"
            size="large"
          >
            <InfoCircleOutlined
              style={{ color: token.colorTextSecondary, cursor: "help" }}
            />
          </CustomTooltip>
        </Space>
      </div>
    );
  } else {
    return <></>;
  }
}

const reasonVerbiageTemplate = {
  noRole: {
    IngestingButEntityNotInUse: (r: Reason) =>
      `Integration ${r.integration} is ingesting, but the entity appears not to be in use.`,
    IngestionEarly: (r: Reason) =>
      `Integration ${r.integration} is ingesting and may not have populated the entity yet.`,
    ConfigurationError: (r: Reason) =>
      `Integration ${r.integration} is experiencing errors, possibly misconfigured.`,
    IngestionNotStarted: (r: Reason) =>
      `Integration ${r.integration} has not started ingesting yet.`,
    NothingIngested: (r: Reason) =>
      `No data has been ingested for integration ${r.integration}.`,
  },
  role: {
    IngestingButEntityNotInUse: (r: Reason) =>
      `Integration ${r.integration} is ingesting, but the entity appears not to be in use (relevant only if you use ${r.integration} as a ${r.role}).`,
    IngestionEarly: (r: Reason) =>
      `Integration ${r.integration} is ingesting and may not have populated the entity yet (relevant only if you use ${r.integration} as a ${r.role}).`,
    ConfigurationError: (r: Reason) =>
      `Integration ${r.integration} is experiencing errors, possibly misconfigured (relevant only if you use ${r.integration} as a ${r.role}).`,
    IngestionNotStarted: (r: Reason) =>
      `Integration ${r.integration} has not started ingesting yet (relevant only if you use ${r.integration} as a ${r.role}).`,
    NothingIngested: (r: Reason) =>
      `No data has been ingested for integration ${r.integration} (relevant only if you use ${r.integration} as a ${r.role}).`,
  },
};

function reasonToVerbiage(reason: Reason) {
  const t = reasonVerbiageTemplate[reason.role ? "role" : "noRole"] as Record<
    string,
    (r: Reason) => string
  >;
  return t[reason.code](reason);
}
